import React, { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTarget, FiMail, FiKeyRound, FiLogIn } = FiIcons;

const TURNSTILE_SCRIPT_ID = 'bpa-turnstile-script';
let turnstileLoadPromise;

function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (turnstileLoadPromise) return turnstileLoadPromise;

  turnstileLoadPromise = new Promise((resolve, reject) => {
    let script = document.getElementById(TURNSTILE_SCRIPT_ID);
    const timeout = window.setTimeout(() => reject(new Error('Turnstile timed out while loading.')), 15000);
    const loaded = () => {
      window.clearTimeout(timeout);
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error('Turnstile did not become available.'));
    };
    const failed = () => {
      window.clearTimeout(timeout);
      reject(new Error('Turnstile could not be loaded.'));
    };

    if (!script) {
      script = document.createElement('script');
      script.id = TURNSTILE_SCRIPT_ID;
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.addEventListener('load', loaded, { once: true });
      script.addEventListener('error', failed, { once: true });
      document.head.appendChild(script);
      return;
    }
    script.addEventListener('load', loaded, { once: true });
    script.addEventListener('error', failed, { once: true });
  }).catch((error) => {
    turnstileLoadPromise = undefined;
    throw error;
  });

  return turnstileLoadPromise;
}

const Login = () => {
  const { user, login, requestLoginCode } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    code: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [codeRequested, setCodeRequested] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileReady, setTurnstileReady] = useState(false);
  const [turnstileError, setTurnstileError] = useState('');
  const turnstileContainer = useRef(null);
  const turnstileWidget = useRef(null);

  useEffect(() => {
    if (codeRequested || !turnstileContainer.current) return undefined;
    let cancelled = false;
    setTurnstileToken('');
    setTurnstileReady(false);
    setTurnstileError('');

    const initialise = async () => {
      try {
        const turnstile = await loadTurnstile();
        const configResponse = await fetch('/api/config').catch(() => null);
        const config = configResponse?.ok ? await configResponse.json().catch(() => ({})) : {};
        if (cancelled || !turnstileContainer.current || turnstileWidget.current !== null) return;
        turnstileWidget.current = turnstile.render(turnstileContainer.current, {
          sitekey: config.turnstileSiteKey || import.meta.env.VITE_TURNSTILE_SITE_KEY || '0x4AAAAAAEXhf7EqJxyutJ0Q',
          theme: 'light',
          action: 'request_login_code',
          callback: (token) => {
            if (cancelled) return;
            setTurnstileToken(token);
            setTurnstileReady(Boolean(token));
            setTurnstileError('');
          },
          'expired-callback': () => {
            if (cancelled) return;
            setTurnstileToken('');
            setTurnstileReady(false);
          },
          'error-callback': () => {
            if (cancelled) return;
            setTurnstileToken('');
            setTurnstileReady(false);
            setTurnstileError('The security check could not be completed. Please refresh and try again.');
          }
        });
      } catch {
        if (!cancelled) {
          setTurnstileToken('');
          setTurnstileReady(false);
          setTurnstileError('The security check could not be loaded. Please refresh and try again.');
        }
      }
    };
    initialise();
    return () => {
      cancelled = true;
      if (turnstileWidget.current !== null && window.turnstile) {
        window.turnstile.remove(turnstileWidget.current);
        turnstileWidget.current = null;
      }
    };
  }, [codeRequested]);

  if (user) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!codeRequested) {
      if (!turnstileToken) {
        setError('Please complete the security check before requesting a code.');
        setLoading(false);
        return;
      }
      const sent = await requestLoginCode(formData.email, turnstileToken);
      if (sent) { setCodeRequested(true); setError(''); }
      else {
        setError('We could not send the sign-in email. Please try again.');
        setTurnstileToken('');
        setTurnstileReady(false);
        if (turnstileWidget.current !== null && window.turnstile) window.turnstile.reset(turnstileWidget.current);
      }
      setLoading(false);
      return;
    }
    const success = await login(formData.email, formData.code);

    if (!success) {
      setError('Invalid email or six-digit access code');
    }

    setLoading(false);
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <SafeIcon icon={FiTarget} className="w-10 h-10 text-primary-600" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">BPA</h1>
              <p className="text-primary-600 font-medium">Success Center</p>
            </div>
          </div>
          <p className="text-gray-600">Digital Marketing Coach Platform</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <div className="relative">
              <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="you@example.com"
                required
              />
            </div>
          </div>

          {codeRequested && <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Six-digit access code
            </label>
            <div className="relative">
              <SafeIcon icon={FiKeyRound} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="password"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength="6"
                name="code"
                value={formData.code}
                onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.replace(/\D/g, '') }))}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="••••••"
                required
              />
            </div>
          </div>}

          {!codeRequested && <div ref={turnstileContainer} />}

          {!codeRequested && turnstileError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {turnstileError}
            </div>
          )}

          {error && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm"
            >
              {error}
            </motion.div>
          )}

          <button
            type="submit"
            disabled={loading || (!codeRequested && (!turnstileReady || !turnstileToken))}
            className="w-full bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <>
                <SafeIcon icon={FiLogIn} className="w-5 h-5" />
                <span>{codeRequested ? 'Sign In' : 'Email me a code'}</span>
              </>
            )}
          </button>
        </form>

        <div className="mt-8 p-4 bg-gray-50 rounded-lg text-center">
          <p className="text-sm text-gray-600">
            {codeRequested ? 'Enter the six-digit code sent to your email. It expires in 10 minutes.' : 'We will send a one-time sign-in code to your email.'}
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;
